import { parseStreamError } from './errors';

export type DataStreamEvent =
	| { type: 'text'; data: string }
	| { type: 'data'; data: Record< string, unknown >[] };

/**
 * Processes one Vercel AI SDK Data Stream Protocol line.
 *
 * Each line has the format `<prefix>:<json>\n`:
 * - `0:"text"` - text chunk
 * - `2:[{...}]` - data payload
 * - `3:"error message"` or structured error - throws a classified Error
 */
export function processStreamLine( line: string ): DataStreamEvent | null {
	if ( ! line ) {
		return null;
	}

	const colonIndex = line.indexOf( ':' );
	if ( colonIndex === -1 ) {
		return null;
	}

	const prefix = line.slice( 0, colonIndex );
	const jsonValue = line.slice( colonIndex + 1 );

	if ( prefix === '0' ) {
		return { type: 'text', data: JSON.parse( jsonValue ) as string };
	}

	if ( prefix === '2' ) {
		return { type: 'data', data: JSON.parse( jsonValue ) as Record< string, unknown >[] };
	}

	if ( prefix === '3' ) {
		throw parseStreamError( jsonValue );
	}

	return null;
}

/**
 * Parses a Vercel AI SDK Data Stream Protocol response body.
 */
export async function* parseDataStream(
	reader: ReadableStreamDefaultReader< Uint8Array >
): AsyncGenerator< DataStreamEvent, void, unknown > {
	const decoder = new TextDecoder();
	let buffer = '';

	try {
		while ( true ) {
			const { done, value } = await reader.read();
			if ( done ) {
				break;
			}

			buffer += decoder.decode( value, { stream: true } );
			const lines = buffer.split( '\n' );
			buffer = lines.pop() || '';

			for ( const line of lines ) {
				const event = processStreamLine( line );
				if ( event ) {
					yield event;
				}
			}
		}

		if ( buffer ) {
			const event = processStreamLine( buffer );
			if ( event ) {
				yield event;
			}
		}
	} finally {
		reader.releaseLock();
	}
}
